import { FileSearchOutlined, FileTextOutlined } from '@ant-design/icons';
import type { ResponseGetReports } from '@vindr/services/apis/report.api';
import { GENDER, LANGUAGES } from '@vindr/services/constants/common.constant';
import { REPORT_VARIABLES } from '@vindr/services/constants/report.constant';
import { convertPhoneNumber } from '@vindr/utils/formatText';
import {
  generateShareLink,
  generateVisitLink,
  getAccessKeys,
} from '@vindr/utils/report/generateLink';
import { Button, Modal, Spin } from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import QRCode from 'qrcode';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { DraggableData, DraggableEvent } from 'react-draggable';
import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next';
import { ResizableBox } from 'react-resizable';
import './ReportViewport.css';

const INIT_REPORT_Z_INDEX = 600;
function ReportViewport({
  userAuthenticationService,
  httpClientService,
  modalWidth = 680,
  isActive = true,
  study,
}: {
  userAuthenticationService: any;
  httpClientService: any;
  modalWidth: number;
  isActive: boolean;
  study: any;
}) {
  const { t } = useTranslation('Vindoc');
  const [isOpen, setIsOpen] = useState(false);
  const btnRef = useRef<HTMLElement>();
  const btnClientRect: any = btnRef?.current?.getBoundingClientRect() || {};

  return (
    <div>
      <Button
        ref={btnRef}
        onClick={() => {
          setIsOpen(true);
        }}
        icon={<FileTextOutlined className="m-auto" />}
        type="text"
        size="small"
        className="flex items-center justify-center"
        title={t('Report')}
      />
      {isOpen && (
        <ReportModal
          userAuthenticationService={userAuthenticationService}
          httpClientService={httpClientService}
          isOpen={isOpen}
          onCancel={() => setIsOpen(false)}
          modalWidth={modalWidth}
          position={{
            top: `${(btnClientRect.bottom || 20) + 12}px`,
            left: `${(btnClientRect.right || modalWidth) - modalWidth}px`,
          }}
          isActive={isActive}
          study={study}
        />
      )}
    </div>
  );
}

ReportViewport.propTypes = {
  userAuthenticationService: PropTypes.any.isRequired,
  modalWidth: PropTypes.number,
  isActive: PropTypes.bool,
  study: PropTypes.object,
};

export default React.memo(ReportViewport);

function ReportModal({
  userAuthenticationService,
  httpClientService,
  isOpen,
  onCancel,
  position = {},
  modalWidth,
  isActive,
  study,
}) {
  const { t, i18n } = useTranslation('Vindoc');
  const { t: tReport } = useTranslation('Report');
  const DATE_FORMAT = i18n.language === LANGUAGES.VI ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
  const DATE_TIME_FORMAT = `${DATE_FORMAT} HH:mm:ss`;

  const [disabled, setDisabled] = useState(true);
  const [isResizing, setIsResizing] = useState(false);
  const [isFetchingReport, setIsFetchingReport] = useState(true);
  const [reportContent, setReportContent] = useState();
  const [bounds, setBounds] = useState({
    left: 0,
    top: 0,
    bottom: 0,
    right: 0,
  });
  const [resizeDimension, setResizeDimension] = useState({
    width: modalWidth,
    height: 500,
  });
  const [dragPosition, setDragPosition] = useState({
    x: 0,
    y: 0,
  });

  const draggleRef = useRef<HTMLDivElement>(null);
  let modalZIndex = INIT_REPORT_Z_INDEX;
  const { studyInstanceUid, date: studyDateTime, description, modalities } = study || {};

  const studyInfo = useMemo(() => {
    const arrStudyInfo = [];
    if (description && description !== '*') {
      arrStudyInfo.push(description);
    }
    if (studyDateTime) {
      arrStudyInfo.push(studyDateTime);
    }
    return arrStudyInfo.join(' | ');
  }, [description, studyDateTime]);

  useEffect(() => {
    handleFetchReport(studyInstanceUid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyInstanceUid]);

  if (isActive) {
    const numOpenReportModal = document.querySelectorAll('.report-draggable-modal') || [];
    modalZIndex = INIT_REPORT_Z_INDEX + numOpenReportModal.length;
  } else {
    modalZIndex = INIT_REPORT_Z_INDEX;
  }

  const handleFetchInstitution = async (risBaseUrl, institutionId) => {
    const resInstitutions = await fetch(`${risBaseUrl}/institutions/v1/${institutionId}`, {
      headers: userAuthenticationService?.getAuthorizationHeader(),
    });
    const { data } = await resInstitutions.json();
    return data;
  };

  const handleFetchApprover = async (risBaseUrl, reportId) => {
    const resApprover = await fetch(`${risBaseUrl}/reports/v1/approver/${reportId}`, {
      headers: userAuthenticationService?.getAuthorizationHeader(),
    });
    const { data } = await resApprover.json();
    return data;
  };

  const handleFetchReport = async (studyInstanceUid) => {
    try {
      const risBaseUrl = window?.config?.risURL;
      if (!risBaseUrl) return;
      setIsFetchingReport(true);
      const response = (await httpClientService.getReport(studyInstanceUid)) as ResponseGetReports;
      const { data = [] } = response;

      const latestReport = data?.[0];
      if (!latestReport) {
        setIsFetchingReport(false);
        return;
      }

      const approver = await handleFetchApprover(window?.config?.risURL, latestReport?.id);

      latestReport.approver = approver;
      // const institutionId = latestReport?.institution_id;
      // const reportId = latestReport?.id;
      const { study_access_key, visit_access_key } =
        getAccessKeys(latestReport.share_access_keys) || {};
      const shareUrl = generateShareLink(
        study_access_key?.study_instance_uid || studyInstanceUid,
        study_access_key?.key,
        modalities
      );
      const shareVisit = generateVisitLink({
        visit_id: visit_access_key?.visit_id,
        access_key: visit_access_key?.key,
      });
      setIsFetchingReport(false);

      const qrRes = (await QRCode.toDataURL(shareUrl).catch(console.error)) || '';
      const QRShareVisit = (await QRCode.toDataURL(shareVisit).catch(console.error)) || '';

      transformReportContent(latestReport || ({} as any), qrRes, QRShareVisit);
    } catch (error) {
      setIsFetchingReport(false);
    } finally {
      setIsFetchingReport(false);
    }
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const transformReportContent = (
    report: ResponseGetReports['data'][number],
    qrBase64: string,
    QRShareVisit: string
  ) => {
    const {
      content: ReportContent,
      conclusion: ReportConclusion,
      updated_at,
      submitter,
      approver,
      institution,
      procedure,
      order,
      visited_patient,
      layout,
      share_access_keys,
    } = report;
    const { signature, title, fullname } = approver ?? {};
    const approverName = `${title ? title + ' ' : ''}${fullname}`;
    const { title: smTitle, fullname: smName } = submitter ?? {};
    const submitterName = `${smTitle ? smTitle + ' ' : ''}${smName}`;
    const layoutContent = layout?.content || ReportContent || '';
    const { visit_access_key } = getAccessKeys(share_access_keys);

    const {
      logo: InstitutionLogo,
      name: InstitutionName,
      address: InstitutionAddress,
      phone: InstitutionPhone,
      email: InstitutionEmail,
    } = institution ?? {};

    const {
      order_doctor_name: OrderDoctorName,
      refer_doctor_name: ReferDoctorName,
      diagnostic: Diagnosis,
      scheduled_date,
    } = order ?? {};

    const {
      patient_id: PatientID,
      visit_id: VisitID,
      patient_name: PatientName,
      birth_date: PatientBirthday,
      sex: PatientSex,
      address: PatientAddress,
      phone: PatientPhone,
      birth_date,
    } = visited_patient ?? {};

    let PatientAge = '';
    if (scheduled_date && birth_date) {
      PatientAge = `${dayjs(scheduled_date).diff(dayjs(birth_date), 'years')}Y`;
    }

    const convertKeys = [
      [REPORT_VARIABLES.ReportContent.code, ReportContent || ''],
      [REPORT_VARIABLES.ReportConclusion.code, ReportConclusion || ''],
      [REPORT_VARIABLES.StudyDateTime.code, studyDateTime || ''],
      [
        REPORT_VARIABLES.ReportDateTime.code,
        updated_at ? dayjs(updated_at).format(DATE_TIME_FORMAT) : '',
      ],
      [REPORT_VARIABLES.Approver.code, approverName || ''],
      [REPORT_VARIABLES.Submitter.code, submitterName || ''],
      [
        REPORT_VARIABLES.InstitutionLogo.code,
        InstitutionLogo
          ? `<img style="max-height: 100px; margin: 0 auto; padding: 2px;" src="${InstitutionLogo}" />`
          : '',
      ],
      [REPORT_VARIABLES.InstitutionName.code, InstitutionName || ''],
      [REPORT_VARIABLES.InstitutionAddress.code, InstitutionAddress || ''],
      [REPORT_VARIABLES.InstitutionPhone.code, InstitutionPhone || ''],
      [REPORT_VARIABLES.InstitutionEmail.code, InstitutionEmail || ''],
      [REPORT_VARIABLES.ProcedureName.code, procedure?.name || ''],
      [REPORT_VARIABLES.OrderDoctorName.code, OrderDoctorName || ''],
      [REPORT_VARIABLES.ReferDoctorName.code, ReferDoctorName || ''],
      [REPORT_VARIABLES.Diagnosis.code, Diagnosis || ''],
      [REPORT_VARIABLES.PatientID.code, PatientID || ''],
      [REPORT_VARIABLES.VisitID.code, VisitID || ''],
      [REPORT_VARIABLES.PatientName.code, PatientName || ''],
      [
        REPORT_VARIABLES.PatientBirthday.code,
        PatientBirthday ? dayjs(PatientBirthday).format(DATE_FORMAT) : '',
      ],
      [REPORT_VARIABLES.PatientAge.code, PatientAge ?? ''],
      [
        REPORT_VARIABLES.PatientSex.code,
        PatientSex || PatientSex === 0 ? tReport(GENDER[PatientSex]) : '',
      ],
      [REPORT_VARIABLES.PatientAddress.code, PatientAddress || ''],
      [REPORT_VARIABLES.PatientPhone.code, convertPhoneNumber(PatientPhone)],
      [
        REPORT_VARIABLES.Signature.code,
        signature
          ? `<img style="max-height: 100px; margin: 0 auto; padding: 2px;" src=${signature} />`
          : '',
      ],
      [
        REPORT_VARIABLES.QRCode.code,
        qrBase64 ? `<img style="padding: 2px" src="${qrBase64}" />` : '',
      ],
      [
        REPORT_VARIABLES.Password.code,
        PatientPhone ? `${PatientPhone.substring(PatientPhone.length - 4)}a@` : '',
      ],
      [REPORT_VARIABLES.Today.code, dayjs().format(DATE_FORMAT)],
      [
        REPORT_VARIABLES.PatientBirthdayYear.code,
        PatientBirthday ? dayjs(PatientBirthday).format('YYYY') : '',
      ],
      [
        REPORT_VARIABLES.QRVisit.code,
        QRShareVisit ? `<img style="padding: 2px" src="${QRShareVisit}" />` : '',
      ],
      [REPORT_VARIABLES.QRVisitPassword.code, visit_access_key?.password || ''],
    ];

    const layoutTransformed = transformData(convertKeys, layoutContent);
    setReportContent(layoutTransformed);
  };

  const transformData = (convertKeys = [], data = '') => {
    return convertKeys.reduce((acc, [key, value]) => {
      return acc.replaceAll(key, value);
    }, data);
  };

  const onStart = (_event: DraggableEvent, uiData: DraggableData) => {
    const { clientWidth, clientHeight } = window.document.documentElement;
    const targetRect = draggleRef.current?.getBoundingClientRect();
    if (!targetRect) {
      return;
    }
    setIsResizing(true);
    setBounds({
      left: -targetRect.left + uiData.x,
      right: clientWidth - (targetRect.right - uiData.x),
      top: -targetRect.top + uiData.y,
      bottom: clientHeight - (targetRect.bottom - uiData.y),
    });
  };

  const handleOnDrag = (_event: DraggableEvent, uiData: DraggableData) => {
    setDragPosition({ x: uiData.x, y: uiData.y });
  };

  const handleResizeWindow = useCallback(() => {
    setDisabled(false);
    triggerMouseEvent(draggleRef.current, 'mouseover');
    triggerMouseEvent(draggleRef.current, 'mousedown');
    triggerMouseEvent(document, 'mousemove');
    triggerMouseEvent(draggleRef.current, 'mouseup');
    triggerMouseEvent(draggleRef.current, 'click');
    setDisabled(true);
  }, []);

  useLayoutEffect(() => {
    window.addEventListener('resize', handleResizeWindow);
    return () => window.removeEventListener('resize', handleResizeWindow);
  }, [handleResizeWindow]);

  const triggerMouseEvent = (element, eventType) => {
    const mouseEvent = new Event(eventType, {
      bubbles: true,
      cancelable: true,
    });
    element.dispatchEvent(mouseEvent);
  };

  return (
    <div>
      <Modal
        width={resizeDimension.width}
        title={
          <div
            style={{
              width: '100%',
              cursor: 'move',
            }}
            onMouseOver={() => {
              if (disabled) {
                setDisabled(false);
              }
            }}
            onMouseOut={() => {
              setDisabled(true);
            }}
            // fix eslintjsx-a11y/mouse-events-have-key-events
            // https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/master/docs/rules/mouse-events-have-key-events.md
            onFocus={() => {}}
            onBlur={() => {}}
            // end
          >
            <div className="select-none">{t('Report')}</div>
            <div
              className="select-none overflow-hidden text-ellipsis whitespace-nowrap pr-[28px] text-sm"
              title={studyInfo}
            >
              {studyInfo}
            </div>
          </div>
        }
        mask={false}
        maskClosable={false}
        open={isOpen}
        destroyOnClose={true}
        onCancel={onCancel}
        footer={null}
        style={position}
        zIndex={modalZIndex}
        wrapClassName={`report-draggable-modal${isActive ? ' is-active-modal' : ''}${
          isResizing ? ' is-resizing' : ''
        }`}
        modalRender={(modal) => (
          <Draggable
            disabled={disabled}
            bounds={bounds}
            nodeRef={draggleRef}
            onStart={(event, uiData) => onStart(event, uiData)}
            position={dragPosition}
            onDrag={handleOnDrag}
            onStop={() => setIsResizing(false)}
          >
            <div ref={draggleRef}>{modal}</div>
          </Draggable>
        )}
      >
        <Spin spinning={isFetchingReport} tip={t('Loading...')}>
          <ResizableBox
            minConstraints={[300, 200]}
            width={resizeDimension.width}
            height={resizeDimension.height}
            onResize={(_, { size, handle }) => {
              if (handle === 'sw') {
                const deltaWidth = size.width - resizeDimension.width;
                const newPos = { ...dragPosition };
                newPos.x -= deltaWidth;
                setDragPosition(newPos);
              }
              setResizeDimension({
                width: size?.width || 680,
                height: size?.height || 500,
              });
            }}
            onResizeStart={() => setIsResizing(true)}
            onResizeStop={() => setIsResizing(false)}
            resizeHandles={['se', 'sw']}
          >
            <div className="report-vp-content flex min-h-[120px] flex-col p-2 ">
              {!isFetchingReport &&
                (reportContent ? (
                  <div className="text-editor">
                    <div dangerouslySetInnerHTML={{ __html: reportContent }}></div>
                  </div>
                ) : (
                  <div className="m-auto flex flex-col items-center py-[32px]">
                    <div className="mb-2 text-[36px] text-[#6c6c6c]">
                      <FileSearchOutlined />
                    </div>
                    <div>{t("The report isn't ready yet")}</div>
                  </div>
                ))}
            </div>
          </ResizableBox>
        </Spin>
      </Modal>
    </div>
  );
}

ReportModal.propTypes = {
  userAuthenticationService: PropTypes.any.isRequired,
  modalWidth: PropTypes.number,
  isOpen: PropTypes.bool,
  onCancel: PropTypes.func,
  position: PropTypes.object,
  isActive: PropTypes.bool,
  study: PropTypes.any,
};
